import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import Cal, { getCalApi } from "@calcom/embed-react";
import axios from "axios";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Calendar,
  Mail,
  ArrowUpRight,
  Home,
  MapPin,
  Clock,
  Utensils,
  BookOpen,
  MessageCircle,
  Download,
  ShieldCheck,
  User,
} from "lucide-react";
import { getCheckoutStatus } from "../lib/api";
import Navbar from "./Navbar";
import Footer from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_POLLS = 8;
const POLL_INTERVAL = 2000;

export default function CheckoutSuccessPage({ onOpenBooking }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");

  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  const pollStatus = useCallback(
    async (attempts = 0) => {
      if (!sessionId) {
        setStatus("error");
        setError("session_id mancante nell'URL");
        return;
      }
      if (attempts >= MAX_POLLS) {
        setStatus("pending");
        return;
      }
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          setOrder(data);
          setStatus("paid");
          // GA4 purchase event (guarded to fire only once per session_id).
          try {
            const key = `ga4_purchase_${sessionId}`;
            if (!sessionStorage.getItem(key)) {
              const { trackPurchase } = await import("../lib/analytics");
              trackPurchase({
                sessionId,
                productId: data.product_id,
                productName: data.product_name,
                amount: data.amount_total ? data.amount_total / 100 : data.amount || 0,
              });
              sessionStorage.setItem(key, "1");
            }
          } catch (_) { /* analytics failure never breaks flow */ }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(() => pollStatus(attempts + 1), POLL_INTERVAL);
      } catch (e) {
        setStatus("error");
        setError(e.response?.data?.detail || e.message);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Grazie per il tuo acquisto — MyDigitalAction";
    pollStatus(0);
  }, [pollStatus]);

  return (
    <div className="App grain min-h-screen">
      <Navbar onOpenBooking={onOpenBooking} />

      {status === "checking" && <StateChecking />}
      {status === "pending" && <StatePending />}
      {status === "expired" && <StateExpired onBack={() => navigate(-1)} />}
      {status === "error" && <StateError error={error} />}
      {status === "paid" && order && <StatePaid order={order} onOpenBooking={onOpenBooking} />}

      <Footer />
    </div>
  );
}

function StateChecking() {
  return (
    <section className="pt-40 pb-24">
      <div className="container-max text-center max-w-xl mx-auto">
        <Loader2 size={48} className="animate-spin text-white/70 mx-auto mb-6" />
        <h1 className="font-display text-3xl lg:text-4xl text-white">Sto verificando il pagamento...</h1>
        <p className="text-white/60 mt-4">Un attimo di pazienza, ci vogliono pochi secondi.</p>
      </div>
    </section>
  );
}

function StatePending() {
  return (
    <section className="pt-40 pb-24">
      <div className="container-max text-center max-w-xl mx-auto">
        <Loader2 size={48} className="text-yellow-400/80 mx-auto mb-6" />
        <h1 className="font-display text-3xl lg:text-4xl text-white">Il pagamento è in elaborazione</h1>
        <p className="text-white/60 mt-4">
          Riceverai un'email di conferma appena il pagamento sarà confermato dalla banca.
          Puoi chiudere questa pagina in sicurezza.
        </p>
      </div>
    </section>
  );
}

function StateExpired({ onBack }) {
  return (
    <section className="pt-40 pb-24">
      <div className="container-max text-center max-w-xl mx-auto">
        <XCircle size={48} className="text-red-400 mx-auto mb-6" />
        <h1 className="font-display text-3xl lg:text-4xl text-white">Sessione scaduta</h1>
        <p className="text-white/60 mt-4">La sessione di pagamento è scaduta. Riprova pure.</p>
        <button onClick={onBack} className="btn-primary mt-8">Torna indietro</button>
      </div>
    </section>
  );
}

function StateError({ error }) {
  return (
    <section className="pt-40 pb-24">
      <div className="container-max text-center max-w-xl mx-auto">
        <XCircle size={48} className="text-red-400 mx-auto mb-6" />
        <h1 className="font-display text-3xl lg:text-4xl text-white">Qualcosa è andato storto</h1>
        <p className="text-white/60 mt-4">{error || "Contatta il supporto se il problema persiste."}</p>
        <Link to="/" className="btn-primary mt-8 inline-flex">
          <Home size={16} /> Torna alla home
        </Link>
      </div>
    </section>
  );
}

function StatePaid({ order, onOpenBooking }) {
  const isAllIn = order.product_id === "all-in-experience";
  const isBundle = order.product_id?.startsWith("bundle:");
  const productLabel = isAllIn
    ? "All-In Experience — 5 corsi completi"
    : isBundle
    ? "Bundle 2 corsi"
    : order.product_id;

  const [onboardingUrl, setOnboardingUrl] = useState(null);

  useEffect(() => {
    axios.get(`${API}/site-config`).then((r) => {
      setOnboardingUrl(r.data.cal_onboarding_url);
    }).catch(() => {
      setOnboardingUrl("https://cal.com/frank-gheri/onboarding-mydigitalaction-45m");
    });
  }, []);

  useEffect(() => {
    (async () => {
      const cal = await getCalApi({ namespace: "onboarding-success" });
      cal("ui", {
        theme: "dark",
        hideEventTypeDetails: false,
        cssVarsPerTheme: {
          dark: { "cal-brand": "#d4ff3d" },
        },
      });
    })();
  }, []);

  // Derive Cal.com link path (username/slug) from full URL
  const calLink = onboardingUrl ? onboardingUrl.replace(/^https?:\/\/(app\.)?cal\.com\//, "") : "";

  return (
    <>
      {/* HERO SUCCESS */}
      <section className="pt-32 lg:pt-40 pb-12 lg:pb-16 relative overflow-hidden">
        <div className="absolute inset-0 gradient-glow pointer-events-none opacity-60" />
        <div className="container-max relative">
          <div className="max-w-3xl mx-auto text-center">
            <div
              className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-8"
              style={{ background: "#d4ff3d", boxShadow: "0 0 50px rgba(212,255,61,0.35)" }}
            >
              <CheckCircle2 size={44} className="text-black" strokeWidth={2.5} />
            </div>
            <span className="section-label">Pagamento confermato</span>
            <h1 className="font-display text-4xl lg:text-6xl text-white mt-6 leading-[1.05]">
              Ci vediamo in aula!<br />
              <span className="italic font-light text-white/60">Grazie per aver scelto MyDigitalAction.</span>
            </h1>
            <p className="text-white/70 text-base lg:text-lg mt-6 max-w-2xl mx-auto">
              Il tuo posto è confermato. Riceverai a breve una mail con tutti i dettagli logistici,
              i materiali di preparazione e i link ai bonus scaricabili.
            </p>
          </div>
        </div>
      </section>

      {/* ORDER SUMMARY */}
      <section className="pb-8">
        <div className="container-max max-w-3xl">
          <div className="bg-card border border-white/10 rounded-2xl p-6 lg:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-mono-label text-white/40 text-[11px]">Riepilogo ordine</div>
                <div className="font-display text-white text-xl lg:text-2xl mt-2">{productLabel}</div>
                <div className="text-white/40 text-xs mt-2 font-mono">
                  ID: {order.session_id.slice(0, 32)}...
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-white text-4xl">€{order.amount_total.toFixed(2)}</div>
                <div className="font-mono-label text-[10px] mt-1" style={{ color: "#d4ff3d" }}>
                  PAGATO
                </div>
              </div>
            </div>
            {order.customer_email && (
              <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-3 text-white/70 text-sm">
                <Mail size={16} className="text-white/50" />
                <span>
                  Ricevuta e conferma inviate a{" "}
                  <span className="text-white">{order.customer_email}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ONBOARDING BOOKING — inline Cal.com embed */}
      <section className="py-14 lg:py-20">
        <div className="container-max max-w-5xl">
          <div className="text-center mb-8">
            <span className="section-label">Prossimo passo — priorità 1</span>
            <h2 className="font-display text-3xl lg:text-5xl text-white mt-6 leading-tight">
              Prenota subito l'onboarding
            </h2>
            <p className="text-white/60 text-base lg:text-lg mt-5 max-w-2xl mx-auto">
              30 minuti in call con il tuo consulente per definire obiettivi, aspettative
              e la data della tua sessione in aula. È incluso nel corso, non costa nulla in più.
            </p>
          </div>

          <div
            className="bg-card border rounded-3xl overflow-hidden"
            style={{ borderColor: "rgba(212,255,61,0.35)", boxShadow: "0 0 60px rgba(212,255,61,0.06)" }}
            data-testid="onboarding-cal-embed"
          >
            {calLink ? (
              <Cal
                namespace="onboarding-success"
                calLink={calLink}
                style={{ width: "100%", height: "700px", overflow: "scroll" }}
                config={{
                  layout: "month_view",
                  theme: "dark",
                  name: order.customer_name || "",
                  email: order.customer_email || "",
                }}
              />
            ) : (
              <div className="h-[700px] flex items-center justify-center">
                <Loader2 size={28} className="animate-spin text-white/50" />
              </div>
            )}
          </div>

          <div className="mt-6 text-center text-white/50 text-sm">
            Preferisci prenotare più tardi?{" "}
            <Link
              to="/area-studente"
              className="underline text-white/80 hover:text-white"
              data-testid="cta-student-area"
            >
              Vai all'Area Studente
            </Link>
            {" "}— trovi lo stesso calendario, i tuoi materiali e i tuoi corsi.
          </div>
        </div>
      </section>

      {/* NEXT STEPS */}
      <section className="py-14 lg:py-20 bg-[#0c0c0c] border-y border-white/5">
        <div className="container-max max-w-4xl">
          <div className="text-center mb-10">
            <span className="section-label">Cosa succede adesso</span>
            <h2 className="font-display text-3xl lg:text-5xl text-white mt-6 leading-tight">
              I tuoi prossimi passi
            </h2>
          </div>

          <div className="space-y-3">
            <NextStep
              n="01"
              icon={<Calendar size={18} />}
              title="Prenota l'onboarding (qui sopra)"
              desc="Blocca 30 minuti in agenda: definiamo insieme obiettivi e la data della tua sessione in aula."
              action={null}
            />
            <NextStep
              n="02"
              icon={<User size={18} />}
              title="Accedi all'Area Studente"
              desc="Con lo stesso indirizzo email dell'ordine trovi corsi, materiali scaricabili e le date delle prossime edizioni."
              action={
                <Link to="/area-studente" className="btn-secondary" style={{ padding: "10px 18px", fontSize: 13 }}>
                  Entra ora <ArrowUpRight size={14} />
                </Link>
              }
            />
            <NextStep
              n="03"
              icon={<Download size={18} />}
              title="Ricevi materiali e bonus"
              desc="Nell'email di conferma trovi i link ai workbook, alla Strategic Map e ai laboratori pratici. Tutto è anche disponibile nell'Area Studente."
              action={
                <span className="text-white/40 text-xs">Consegnati via email</span>
              }
            />
          </div>
        </div>
      </section>

      {/* LOCATION + INFO CARDS */}
      <section className="py-14 lg:py-20 bg-[#0c0c0c] border-y border-white/5">
        <div className="container-max">
          <div className="text-center mb-10">
            <span className="section-label">Info logistiche</span>
            <h2 className="font-display text-3xl lg:text-5xl text-white mt-6 leading-tight">
              Tutto quello che ti serve sapere
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
            <InfoCard icon={<MapPin size={20} />} label="Dove">
              <div className="font-display text-white text-lg">Montacchiello Campus</div>
              <div className="text-white/60 text-sm mt-1">Via Umberto Forti 6, 56021 Pisa</div>
              <a
                href="https://maps.google.com/?q=Via+Umberto+Forti+6+Montacchiello+Pisa"
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-xs text-white/70 hover:text-white"
              >
                Apri in Google Maps <ArrowUpRight size={11} />
              </a>
            </InfoCard>

            <InfoCard icon={<Clock size={20} />} label="Quando">
              <div className="font-display text-white text-lg">Ti confermiamo</div>
              <div className="text-white/60 text-sm mt-1">
                Sessioni tipicamente 9:00 – 18:00, con pausa pranzo alle 13:00.
              </div>
              <div className="mt-3 text-white/50 text-xs">
                Data personalizzata concordata via call
              </div>
            </InfoCard>

            <InfoCard icon={<Utensils size={20} />} label="Pranzo incluso">
              <div className="font-display text-white text-lg">Main Kitchen</div>
              <div className="text-white/60 text-sm mt-1">
                Light lunch conviviale a due passi dal Campus per creare connessioni.
              </div>
              <div className="mt-3 text-white/50 text-xs">
                Segnalaci in call eventuali intolleranze
              </div>
            </InfoCard>
          </div>

          {/* What to bring */}
          <div className="max-w-3xl mx-auto mt-10 bg-card border border-white/10 rounded-2xl p-6 lg:p-8">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen size={18} className="text-white/60" />
              <div className="font-display text-white text-lg">Cosa portare in aula</div>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Laptop personale (necessario per esercitazioni)",
                "Blocco appunti e penna",
                "Documento di identità",
                "Voglia di applicare, non solo ascoltare",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-white/80 text-sm">
                  <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" style={{ color: "#d4ff3d" }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA + GUARANTEE */}
      <section className="py-14 lg:py-20">
        <div className="container-max max-w-4xl">
          <div className="bg-card border border-white/10 rounded-2xl p-6 lg:p-8">
            <MessageCircle size={22} className="text-lime" style={{ color: "#d4ff3d" }} />
            <div className="font-display text-white text-xl mt-4">Hai una domanda al volo?</div>
            <p className="text-white/60 text-sm mt-3">
              Il nostro team è disponibile su WhatsApp per qualsiasi domanda su corso,
              logistica o accesso ai materiali. Rispondiamo di norma entro poche ore.
            </p>
            <a
              href="https://api.whatsapp.com/send?phone=393204459052&text=Ho%20appena%20acquistato%20e%20avrei%20una%20domanda"
              target="_blank"
              rel="noreferrer"
              className="btn-secondary mt-5"
            >
              Scrivici su WhatsApp <ArrowUpRight size={16} />
            </a>
          </div>

          <div className="mt-6 bg-card border border-white/10 rounded-2xl p-5 flex items-start gap-4">
            <ShieldCheck size={20} className="text-lime flex-shrink-0 mt-0.5" style={{ color: "#d4ff3d" }} />
            <div className="text-white/70 text-sm leading-relaxed">
              <strong className="text-white">Garanzia Zero Rischi:</strong> se dopo la sessione mattutina
              senti che il corso non fa per te, entro le ore 13 potrai lasciare l'aula, ricevere il
              rimborso completo e conservare il workbook come nostro omaggio.
            </div>
          </div>
        </div>
      </section>

      {/* Back link */}
      <section className="pb-16 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm"
        >
          <Home size={14} /> Torna alla home
        </Link>
      </section>
    </>
  );
}

function NextStep({ n, icon, title, desc, action }) {
  return (
    <div className="bg-card border border-white/10 rounded-2xl p-6 lg:p-7 flex items-start gap-5 hover-lift">
      <div className="font-display text-3xl lg:text-4xl text-white/25 min-w-[3rem] leading-none">{n}</div>
      <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 text-white">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-white text-lg lg:text-xl">{title}</div>
        <p className="text-white/60 text-sm mt-2 leading-relaxed">{desc}</p>
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}

function InfoCard({ icon, label, children }) {
  return (
    <div className="bg-card border border-white/10 rounded-2xl p-6 h-full flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white">
          {icon}
        </div>
        <span className="font-mono-label text-white/40 text-[10px]">{label}</span>
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}
